import React, { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Icon } from '@/components/atoms/Icon';
import { ShortlistItemProvider } from '@karehero/models';
import { formatAddress } from '@karehero/helpers/address';

type StatusColor = 'success' | 'warning' | 'error' | 'info' | 'default';

const CHIP_MIN_FONT = { fontSize: '1rem' } as const;

const statusColor = (status: string | undefined | null): StatusColor => {
  switch (status) {
    case 'Secured':
    case 'Available':
      return 'success';
    case 'Waitlist':
    case 'Contacted':
      return 'info';
    case 'Unavailable':
      return 'error';
    default:
      return 'default';
  }
};

// CareSourcer's placeholder, shown when a provider has no photo.
const PHOTO_PLACEHOLDER = '/images/provider-placeholder.png';

const formatRating = (rating?: string): string =>
  rating
    ? rating.charAt(0).toUpperCase() + rating.slice(1).replace(/_/g, ' ')
    : 'Not rated';

const ProviderPhoto = ({
  provider,
  photoIndex,
  onPrev,
  onNext,
}: {
  provider: ShortlistItemProvider;
  photoIndex: number;
  onPrev: () => void;
  onNext: () => void;
}) => {
  const photos = provider.photos || [];
  const photo = photos[photoIndex];
  const hasMultiple = photos.length > 1;

  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.200',
        flexShrink: 0,
      }}
    >
      {hasMultiple && (
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
        >
          <Icon icon='chevron-left' fontSize={12} />
        </IconButton>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          maxHeight: 240,
          aspectRatio: '16 / 9',
        }}
      >
        <CardMedia
          component='img'
          image={photo?.medium || PHOTO_PLACEHOLDER}
          alt=''
          sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      </Box>
      {hasMultiple && (
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
        >
          <Icon icon='chevron-right' fontSize={12} />
        </IconButton>
      )}
    </Stack>
  );
};

const ProviderChips = ({ provider }: { provider: ShortlistItemProvider }) => {
  const chips = useMemo(() => {
    let results = (provider.careServiceTypes || []).map((st) => ({
      label: st.name,
    }));

    if (provider.isPremium === true) {
      const premiumChip = { label: 'Premium', color: 'success' };
      results = [premiumChip, ...results];
    }

    return results;
  }, [provider]);

  return (
    <Stack
      direction='row'
      spacing={1}
      useFlexGap
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {chips.map((chip) => (
        <Chip {...chip} key={chip.label} size='small' sx={CHIP_MIN_FONT} />
      ))}
    </Stack>
  );
};

const ProviderPrice = ({ provider }: { provider: ShortlistItemProvider }) => {
  if (!provider.pricePerWeek) return null;

  return (
    // One size across the line at 16px — the figure carries the emphasis
    // through weight (bodyBold) rather than being a size of its own.
    <Stack
      direction='row'
      spacing={0.5}
      sx={{
        alignItems: 'baseline',
      }}
    >
      <Typography
        variant='body1'
        sx={{
          color: 'text.secondary',
        }}
      >
        from
      </Typography>
      <Typography variant='bodyBold'>
        £{provider.pricePerWeek.toLocaleString()}
      </Typography>
      <Typography
        variant='body1'
        sx={{
          color: 'text.secondary',
        }}
      >
        /week
      </Typography>
    </Stack>
  );
};

export interface CareProviderCardProps {
  provider: ShortlistItemProvider;
  distance?: string;
  status?: string;
  onClick?: () => void;
  onAddNote?: () => void;
  notes?: React.ReactNode;
  actionButton?: React.ReactNode;
}

export const CareProviderCard = ({
  provider,
  distance,
  status,
  onClick,
  onAddNote,
  notes,
  actionButton,
}: CareProviderCardProps) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const photos = provider.photos || [];

  const address = useMemo(() => formatAddress(provider.address), [provider]);

  const prevPhoto = () =>
    setPhotoIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const nextPhoto = () =>
    setPhotoIndex((i) => (i < photos.length - 1 ? i + 1 : 0));

  return (
    // Full height of its grid cell, as a column, so the actions row sits on the
    // bottom edge whatever the content above it measures — Add to shortlist
    // lines up across a row of cards.
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ProviderPhoto
        provider={provider}
        photoIndex={photoIndex}
        onPrev={prevPhoto}
        onNext={nextPhoto}
      />
      {/* Who and where: the tappable half of the card. */}
      <CardActionArea
        onClick={onClick}
        disabled={!onClick}
        sx={{ flexGrow: 1 }}
      >
        {/* Two blocks — who it is, then what it offers — 12px apart. */}
        <Stack
          spacing={2}
          sx={{
            p: 2,
          }}
        >
          {/* name and address read as one block, so they sit tighter than the
                gaps between the other rows */}
          <Stack spacing={1}>
            <Stack
              direction='row'
              spacing={1}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h4'>{provider.name}</Typography>
              {status && (
                <Chip
                  size='small'
                  label={status}
                  color={statusColor(status)}
                  sx={CHIP_MIN_FONT}
                />
              )}
            </Stack>
            <Typography variant='body1' color='text.secondary'>
              {address}
            </Typography>
            {distance && (
              <Typography variant='body1' color='text.secondary'>
                {distance}
              </Typography>
            )}
            <Typography variant='body1' color='text.secondary'>
              Rating:{' '}
              <Typography component='span' variant='bodyBold'>
                {formatRating(provider.rating?.overallRating)}
              </Typography>
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            <ProviderChips provider={provider} />
            <ProviderPrice provider={provider} />
          </Stack>
        </Stack>
      </CardActionArea>
      {/* What you can do about it, divided off by the top stroke. Buttons sit
          16px apart and share the padding of the block above. */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {notes && (
          <Stack
            sx={{
              px: 2,
              pt: 2,
            }}
          >
            {notes}
          </Stack>
        )}
        {(onAddNote || actionButton) && (
          <Stack
            direction='row'
            spacing={2}
            sx={{
              alignItems: 'center',
              px: 2,
              py: 2,
            }}
          >
            {actionButton || (
              <Box
                sx={{
                  flexGrow: 1,
                }}
              />
            )}
            {onAddNote && (
              <Button
                variant='outlined'
                size='small'
                color='primary'
                onClick={onAddNote}
              >
                Add note
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
};

export default CareProviderCard;
